import { useEffect, useState } from 'react';
import { api, fmt } from '../api.ts';
import SplitTable, { type SplitSide as SplitTableSide } from './SplitTable.tsx';

export interface SplitRow {
  job_id: number | null;
  job_number: string | null;
  ordered_quantity: string;
  customer_name: string;
  box_name: string;
  is_staged: boolean;
  has_card: boolean;
  board_paired: string | null;
  sheets_by_hand?: boolean | null;
}

interface SplitSide { quantity: number; run: number; spare: number }
interface SplitPreview { ups: number | null; blocked?: string | null; part: SplitSide | null; rest: SplitSide | null }

interface Result {
  splitId: number;
  part: { id: number; jobNumber: string; quantity: number };
  rest: { jobNumber: string; quantity: number };
}

export default function SplitDialog({ row, onClose, onDone }: {
  row: SplitRow;
  onClose: () => void;
  onDone: (msg: string, splitId: number) => void;
}) {
  const [qty, setQty] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<SplitPreview | null>(null);

  const total = Number(row.ordered_quantity);
  const q = Number(qty);
  const valid = Number.isInteger(q) && q > 0 && q < total;
  const tooMuch = Number.isInteger(q) && q >= total;
  const name = row.job_number ?? '';
  const where = row.is_staged ? 'Staged' : 'the Queue';
  const [blocked, setBlocked] = useState<string | null>(null);

  useEffect(() => {
    api.get<SplitPreview>(`/api/pre-press/jobs/${row.job_id}/split-preview`)
      .then((r) => setBlocked(r?.blocked ?? null))
      .catch(() => setBlocked(null));
  }, [row.job_id]);

  useEffect(() => {
    setPreview(null);
    if (!valid) return;
    const t = setTimeout(() => {
      api.get<SplitPreview>(`/api/pre-press/jobs/${row.job_id}/split-preview?quantity=${q}`)
        .then((r) => setPreview(r?.part && r.rest ? r : null))
        .catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(t);
  }, [valid, q, row.job_id]);

  const sides: SplitTableSide[] | null = valid && preview?.part && preview.rest ? [
    { label: name, boxes: preview.rest.quantity, run: preview.rest.run },
    { label: 'New part', boxes: preview.part.quantity, run: preview.part.run },
  ] : null;
  const changes = !valid ? [] : [
    ...(row.has_card ? ['The card, route and artwork are copied to the new part'] : []),
    ...(Number(row.board_paired ?? 0) > 0 ? ['Held board is shared out by quantity'] : []),
    ...(row.sheets_by_hand ? ['Typed board sheets are divided · check them on both cards'] : []),
    `Both stay in ${where}`,
  ];

  async function go() {
    if (!valid) return;
    setBusy(true); setError('');
    try {
      const r = await api.post<Result>(`/api/pre-press/jobs/${row.job_id}/split`, { quantity: q });
      onDone(`${r.part.jobNumber} made · ${fmt(r.part.quantity)} · ${r.rest.jobNumber} keeps ${fmt(r.rest.quantity)}`,
        r.splitId);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not split');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 420 }} role="dialog" aria-label={`Split ${name}`}
           onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Split {name}</h3>
          <p>{row.customer_name} · {row.box_name} · {fmt(total)}</p>
        </div>
        {blocked ? (
          <>
            <div className="modal-body"><p style={{ margin: 0 }}>{blocked}</p></div>
            <div className="modal-foot"><button className="btn" onClick={onClose}>Close</button></div>
          </>
        ) : (<>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="split-qty">Quantity for the new part</label>
            <input id="split-qty" type="number" min="1" step="1" autoFocus value={qty}
                   onChange={(e) => setQty(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') void go(); }} />
            <div className="hint">{tooMuch ? `Less than ${fmt(total)}` : `of ${fmt(total)}`}</div>
          </div>
          {sides && <SplitTable sides={sides} ups={preview?.ups} />}
          {changes.length > 0 && (
            <ul className="undo-list" style={{ marginTop: 16 }}>
              {changes.map((c) => <li key={c}>{c}</li>)}
            </ul>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void go()} disabled={busy || !valid}>
            {busy ? 'Splitting…' : valid ? `Split off ${fmt(q)}` : 'Split'}
          </button>
        </div>
        </>)}
      </div>
    </div>
  );
}
